import { useEffect, useRef } from 'react';
import { Config } from 'final-form';

import { CreateFunnelValues } from 'src/components/Employer/Funnel/components/CreateFunnelForm';
import { CRUD_FUNNEL_URL, CreateFunnelParams } from 'src/components/Employer/Funnel/fetchers';
import { handleResponseErrors } from 'src/components/Employer/Funnel/utils';
import { useNotification } from 'src/components/Notifications/Provider';
import { FunnelInfo } from 'src/components/VacancyCreate/sections/PublicationType/FunnelSelector/fetchFunnelsList';
import { fetcher } from 'src/utils/fetcher';

type UseCreateFunnel = (params: {
    selectedVacancyIds: number[];
    selectedFunnelId: string;
    handleCloseModal: VoidFunction;
    afterSucces?: (funnel: FunnelInfo) => void;
}) => {
    onSubmit: Config<CreateFunnelValues, CreateFunnelValues>['onSubmit'];
};

const useCreateFunnel: UseCreateFunnel = ({ selectedVacancyIds, selectedFunnelId, handleCloseModal, afterSucces }) => {
    const { addNotification } = useNotification();
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleResult = (funnel: FunnelInfo) => {
        handleCloseModal();
        afterSucces?.(funnel);
    };

    const handleError = (error: unknown): Record<string, string> => {
        if (fetcher.isCancel(error)) {
            return {};
        }

        if (!fetcher.isAxiosError(error)) {
            console.error(error);
            return {};
        }

        return handleResponseErrors(error, addNotification);
    };

    const getRequestParams = (values: CreateFunnelValues): CreateFunnelParams => {
        const params: CreateFunnelParams = {
            funnelName: values.funnelName,
            vacancyIds: selectedVacancyIds,
        };

        if (selectedFunnelId) {
            params.copyFromFunnelId = Number(selectedFunnelId);
        }

        return params;
    };

    const onSubmit = async (values: CreateFunnelValues): Promise<Record<string, string>> => {
        try {
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            const response = await fetcher.post(CRUD_FUNNEL_URL, getRequestParams(values), {
                signal: abortControllerRef.current?.signal,
            });

            handleResult(response.data);
        } catch (error) {
            return handleError(error);
        }

        return {};
    };

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    return { onSubmit };
};

export { useCreateFunnel };
